/**
 * The player's animal: a Container of [body (tintable), details (untinted)], so
 * Blender's two-layer exports drop straight in (CLAUDE.md) and a colour swatch
 * only ever tints the body.
 */
import Phaser from 'phaser';
import { artScale } from './artScale';
import { ensurePlaceholderTexture } from './placeholders';
import { ANIMAL_FRAME_COUNTS, animalFrameKey } from '../data/animals';
import type { AnimalState } from '../types';

// Frame counts and key building are data; they live in src/data/animals.ts so
// Node tooling can read them without loading Phaser.
export { ANIMAL_FRAME_COUNTS, animalAssetKeys, animalFrameKey } from '../data/animals';
export type { AnimalLayer } from '../data/animals';

export const DEFAULT_ANIMAL = 'emperor-penguin';

/** Authored at 160x160; 0.8 keeps the animal comfortably inside a 140px gap. */
export const ANIMAL_SCALE = 0.8;

const IDLE_FRAME_MS = 420;

export class AnimalSprite extends Phaser.GameObjects.Container {
  private readonly bodyLayer: Phaser.GameObjects.Image;
  private readonly detailLayer: Phaser.GameObjects.Image;
  private current: AnimalState = 'idle';
  /** ANIMAL_SCALE adjusted for however this animal's textures were loaded. */
  private readonly unit: number;
  private requested = 1;
  private idleTimer: Phaser.Time.TimerEvent | undefined;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    private readonly animalId: string = DEFAULT_ANIMAL,
    bodyTint: number | null = null,
  ) {
    super(scene, x, y);

    this.ensureFrame('idle', 0);
    this.unit = ANIMAL_SCALE * artScale(scene, animalFrameKey(animalId, 'idle', 0, 'body'));
    // Origin at the feet, so container y is where the animal stands.
    this.bodyLayer = scene.add
      .image(0, 0, animalFrameKey(animalId, 'idle', 0, 'body'))
      .setOrigin(0.5, 1);
    this.detailLayer = scene.add
      .image(0, 0, animalFrameKey(animalId, 'idle', 0, 'details'))
      .setOrigin(0.5, 1);

    this.add([this.bodyLayer, this.detailLayer]);
    this.setScale(this.unit);
    this.setBodyTint(bodyTint);
    scene.add.existing(this);
  }

  /**
   * The scale this sprite actually draws at, resolution factored in.
   *
   * Tweens that set scaleX/scaleY directly must land on this rather than on
   * ANIMAL_SCALE, or the animal jumps to its raw texture size whenever the art
   * was rasterised above 1x.
   */
  get baseScale(): number {
    return this.unit * this.requested;
  }

  /** Named animalState because Container already owns `state`. */
  get animalState(): AnimalState {
    return this.current;
  }

  /** Swaps both layers to a state's frame. Named to avoid GameObject.setState. */
  setAnimalState(state: AnimalState, frame = 0): this {
    this.current = state;
    this.stopIdleLoop();
    this.applyFrame(state, frame);
    if (state === 'idle' || state === 'cheer') this.startFrameLoop(state);
    return this;
  }

  /**
   * Resize relative to the animal's normal size in play.
   *
   * Callers must use this rather than setScale, which would discard the
   * resolution factor and draw the animal at its raw texture size.
   */
  setDisplayScale(scale: number): this {
    this.requested = scale;
    this.setScale(this.unit * scale);
    return this;
  }

  /** null restores the animal's natural colours. */
  setBodyTint(tint: number | null): this {
    if (tint === null) this.bodyLayer.clearTint();
    else this.bodyLayer.setTint(tint);
    return this;
  }

  /** Squash and stretch, in the animal's own scale space. */
  setSquash(scaleX: number, scaleY: number): this {
    this.setScale(this.unit * this.requested * scaleX, this.unit * this.requested * scaleY);
    return this;
  }

  resetSquash(): this {
    this.setScale(this.unit * this.requested);
    return this;
  }

  override destroy(fromScene?: boolean): void {
    this.stopIdleLoop();
    super.destroy(fromScene);
  }

  private applyFrame(state: AnimalState, frame: number): void {
    this.ensureFrame(state, frame);
    this.bodyLayer.setTexture(animalFrameKey(this.animalId, state, frame, 'body'));
    this.detailLayer.setTexture(animalFrameKey(this.animalId, state, frame, 'details'));
  }

  /** Placeholder textures are generated on demand, so any key is safe to use. */
  private ensureFrame(state: AnimalState, frame: number): void {
    ensurePlaceholderTexture(this.scene, animalFrameKey(this.animalId, state, frame, 'body'));
    ensurePlaceholderTexture(this.scene, animalFrameKey(this.animalId, state, frame, 'details'));
  }

  private startFrameLoop(state: AnimalState): void {
    const frames = ANIMAL_FRAME_COUNTS[state];
    if (frames < 2) return;
    let frame = 0;
    this.idleTimer = this.scene.time.addEvent({
      delay: IDLE_FRAME_MS,
      loop: true,
      callback: () => {
        frame = (frame + 1) % frames;
        this.applyFrame(state, frame);
      },
    });
  }

  private stopIdleLoop(): void {
    this.idleTimer?.remove();
    this.idleTimer = undefined;
  }
}
